import { initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  reload,
  sendEmailVerification,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  User,
} from 'firebase/auth';
import type {
  BootstrapPayload,
  DepartmentAccessInput,
  UpdateUserInvitationInput,
  RestaurantUserInvitation,
  EquipmentInput,
  RestaurantEquipment,
  TemperatureUnit,
  TemperatureUnitInput,
  Locale,
  EquipmentIssueStatus,
  InventoryCountStatus,
  InventoryAdjustment,
  InventoryStatus,
  LaborProfile,
  ProductionLaborAssignment,
  ProductionRecurrence,
  QuickInventoryEntryStatus,
  PurchaseOrderStatus,
  RecipeIngredient,
  RecipePortionConfiguration,
  RestaurantAccountPayload,
  RestaurantRecipe,
  RestaurantMembership,
  RestaurantPlan,
  RestaurantRole,
  RestaurantTenant,
  RestaurantUserDirectory,
  RestaurantVendor,
  ShortageStatus,
  SupplyImageAsset,
  SupplyImageSource,
  SupplyItem,
  SupplyRequest,
} from './types';
import type { InventoryUnit, PackageUnit } from './units';
import type { MenuCategory, MenuCopyInput, MenuPayload, MenuRemoveInput, MenuScheduleChange, MenuScheduleInput } from './menuTypes';
import type { MenuDesign, MenuDesignInput, MenuDesignList } from './menuDesignTypes';
import type { FoodTagId, SupplyFoodLabels } from './foodTags';
import type { SupplyIdentityCheckInput, SupplyIdentityCheckResult, SupplyMergeInput, SupplyMergeResult } from './supplyIdentity';
import type { SupplyPackaging } from './unitSystem';
import type { IntegrationsApi } from './integrationTypes';

export type SupplyFoodLabelsInput = Pick<SupplyFoodLabels, 'tags' | 'reviewed' | 'labelText' | 'source'>;
export interface SupplyItemInput {
  name: { en: string; es: string }; sku?: string; categoryId: string; departmentIds?: string[];
  purchaseUnit: PackageUnit; inventoryUnit: InventoryUnit; quantityPerPackage: number; quantityPerPackageExact?: string;
  parLevel: number; packageCost: number; packageCostExact?: string; packaging?: SupplyPackaging | null;
  image?: SupplyImageAsset; foodLabels?: SupplyFoodLabelsInput;
}
export interface RecipeInput {
  departmentId: string; name: { en: string; es: string }; yieldQuantity: number; yieldQuantityExact?: string; yieldUnit: string;
  portion: RecipePortionConfiguration | null; sellingPrice?: number | null; sellingPriceExact?: string;
  foodCostTargetPercent?: number | null; foodCostTargetPercentExact?: string;
  ingredients: Array<Pick<RecipeIngredient, 'itemId' | 'quantity'> & { quantityExact?: string; quantityInput?: string; unit?: string }>;
  instructions?: string; image?: SupplyImageAsset; active?: boolean;
}

export const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const firebaseApp = demoMode ? null : initializeApp(firebaseConfig);
export const auth = firebaseApp ? getAuth(firebaseApp) : null;
const isRestaurantStagingHost =
  window.location.hostname === 'restaurant-staging.intertaxblock.com'
  || window.location.hostname.includes('restaurant-supply-staging');
export const globalAccountOrigin = (
  import.meta.env.VITE_GLOBAL_ACCOUNT_ORIGIN
  || (isRestaurantStagingHost ? 'https://account-staging.intertaxblock.com' : 'https://account.intertaxblock.com')
).replace(/\/$/, '');

let demoRole: RestaurantRole = (localStorage.getItem('restaurant-demo-role') as RestaurantRole) || 'MANAGER';
let restaurantId = localStorage.getItem('restaurant-id') || 'demo-restaurant';

export function setDemoRole(role: RestaurantRole) {
  demoRole = role;
  localStorage.setItem('restaurant-demo-role', role);
}

export function getDemoRole() {
  return demoRole;
}

export function setRestaurantId(value: string) {
  restaurantId = value;
  localStorage.setItem('restaurant-id', value);
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');
  headers.set('X-Restaurant-Id', restaurantId);
  if (demoMode) headers.set('X-Demo-Role', demoRole);
  else {
    const token = await auth?.currentUser?.getIdToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || `Request failed (${response.status})`), { code: body.code, status: response.status, shortages: body.shortages, field: body.field, matches: body.matches, blockers: body.blockers });
  }
  return response.json();
}

export const integrationsApi: IntegrationsApi = {
  load: () => request('/api/integrations'),
  connect: provider => request(`/api/integrations/${encodeURIComponent(provider)}/oauth/start`, { method: 'POST', body: '{}' }),
  disconnect: provider => request(`/api/integrations/${encodeURIComponent(provider)}/disconnect`, { method: 'POST', body: '{}' }),
  saveLocations: (provider, locationIds) => request(`/api/integrations/${encodeURIComponent(provider)}/locations`, { method: 'PUT', body: JSON.stringify({ locationIds }) }),
  importCatalog: provider => request(`/api/integrations/${encodeURIComponent(provider)}/import`, { method: 'POST', body: '{}' }),
  mapProduct: (id, input) => request(`/api/integrations/mappings/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) }),
  retry: id => request(`/api/integrations/jobs/${encodeURIComponent(id)}/retry`, { method: 'POST', body: '{}' }),
  saveProvider: (id, input) => request(`/api/integrations/providers/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) }),
  uploadLogo: async (id, variant, file) => {
    if (!['image/png', 'image/webp'].includes(file.type) || file.size > 128_000 || !file.size) throw Object.assign(new Error('PNG/WebP only; maximum 128 KB.'), { code: 'INTEGRATION_INVALID_LOGO' });
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = ''; for (const byte of bytes) binary += String.fromCharCode(byte);
    await request(`/api/integrations/providers/${encodeURIComponent(id)}/logos/${variant}`, { method: 'PUT', body: JSON.stringify({ data: btoa(binary), contentType: file.type }) });
  },
};

export async function signIn(email: string, password: string): Promise<User | null> {
  if (demoMode) return null;
  return (await signInWithEmailAndPassword(auth!, email, password)).user;
}

export async function createAccount(displayName: string, email: string, password: string): Promise<User | null> {
  if (demoMode) return null;
  const user = (await createUserWithEmailAndPassword(auth!, email, password)).user;
  await updateProfile(user, { displayName: displayName.trim() });
  await sendEmailVerification(user);
  return user;
}

export async function signInWithGoogle(): Promise<User | null> {
  if (demoMode) return null;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return (await signInWithPopup(auth!, provider)).user;
}

export async function resendVerificationEmail(): Promise<void> {
  if (auth?.currentUser && !auth.currentUser.emailVerified) await sendEmailVerification(auth.currentUser);
}

export async function refreshCurrentUser(): Promise<User | null> {
  if (!auth?.currentUser) return null;
  await reload(auth.currentUser);
  await auth.currentUser.getIdToken(true);
  return auth.currentUser;
}

export async function signOutUser() {
  if (auth) await signOut(auth);
}

async function authenticatedHeaders(json = true): Promise<Headers> {
  const headers = new Headers();
  if (json) headers.set('Content-Type', 'application/json');
  headers.set('X-Restaurant-Id', restaurantId);
  if (demoMode) headers.set('X-Demo-Role', demoRole);
  else {
    const token = await auth?.currentUser?.getIdToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

export async function downloadPurchaseOrderPdf(id: string, fileName: string): Promise<void> {
  const response = await fetch(`/api/purchase-orders/${encodeURIComponent(id)}/pdf`, { headers: await authenticatedHeaders(false) });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = fileName; document.body.appendChild(anchor); anchor.click(); anchor.remove();
  URL.revokeObjectURL(url);
}

export async function downloadEquipmentLabels(ids: string[] = [], locale: Locale = 'en'): Promise<void> {
  const params = new URLSearchParams({ lang: locale });
  if (ids.length) params.set('ids', ids.join(','));
  const response = await fetch(`/api/equipment-labels.pdf?${params}`, { headers: await authenticatedHeaders(false) });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = locale === 'es' ? 'etiquetas-equipos.pdf' : 'equipment-labels.pdf';
  document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
}

export async function completeGlobalSsoFromUrl(): Promise<boolean> {
  if (demoMode || !auth || !window.location.pathname.startsWith('/auth/exchange')) return false;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code') || '';
  const issuer = (params.get('issuer') || '').replace(/\/$/, '');
  if (!code || issuer !== globalAccountOrigin) throw new Error('The global sign-in request is invalid.');
  const response = await fetch(`${globalAccountOrigin}/api/sso/redeem`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body.customToken) throw new Error(body.error || 'Global sign-in could not be completed.');
  await signInWithCustomToken(auth, body.customToken);
  const nextPath = typeof body.nextPath === 'string' && body.nextPath.startsWith('/') ? body.nextPath : '/';
  window.history.replaceState({}, document.title, nextPath);
  const restaurantIdFromPath = new URL(nextPath, window.location.origin).searchParams.get('restaurantId');
  if (restaurantIdFromPath) setRestaurantId(restaurantIdFromPath);
  return true;
}

export function openGlobalAccount() {
  window.location.assign(globalAccountOrigin);
}

export const api = {
  menuDesigns: () => request<MenuDesignList>('/api/menu-designs', { cache: 'no-store' }),
  createMenuDesign: (body: MenuDesignInput) => request<MenuDesign>('/api/menu-designs', { method: 'POST', body: JSON.stringify(body) }),
  updateMenuDesign: (id: string, body: MenuDesignInput & { expectedRevision: number }) => request<MenuDesign>(`/api/menu-designs/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  archiveMenuDesign: (id: string, expectedRevision: number) => request<{ success: true }>(`/api/menu-designs/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ expectedRevision }) }),
  menu: (from: string, to: string) => request<MenuPayload>(`/api/menu?${new URLSearchParams({ from, to })}`, { cache: 'no-store' }),
  initializeMenu: () => request('/api/menu/initialize', { method: 'POST' }),
  createMenuCategory: (body: { name: { en: string; es: string }; sortOrder: number; active?: boolean }) => request<MenuCategory>('/api/menu-categories', { method: 'POST', body: JSON.stringify(body) }),
  updateMenuCategory: (id: string, body: { name: { en: string; es: string }; sortOrder: number; active: boolean; expectedRevision: number }) => request<MenuCategory>(`/api/menu-categories/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createMenuSchedule: (body: MenuScheduleInput) => request('/api/menu-schedules', { method: 'POST', body: JSON.stringify(body) }),
  updateMenuSchedule: (id: string, body: MenuScheduleChange) => request(`/api/menu-schedules/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeMenuSchedule: (id: string, body: MenuRemoveInput) => request(`/api/menu-schedules/${encodeURIComponent(id)}/remove`, { method: 'POST', body: JSON.stringify(body) }),
  copyMenu: (body: MenuCopyInput) => request('/api/menu-copy', { method: 'POST', body: JSON.stringify(body) }),
  account: () => request<RestaurantAccountPayload>('/api/account'),
  createRestaurant: (body: {
    displayName: string;
    name: string;
    legalName?: string;
    billingEmail: string;
    phone: string;
    address: { line1: string; line2?: string; city: string; state: string; postalCode: string };
    timezone: string;
    plan: RestaurantPlan;
  }) => request<{ restaurant: RestaurantTenant; membership: RestaurantMembership }>('/api/onboarding/restaurant', { method: 'POST', body: JSON.stringify(body) }),
  createSubscriptionCheckout: () => request<{
    checkoutSessionId: string;
    apiLoginId: string;
    clientKey: string;
    scriptUrl: string;
    amount: number;
    currency: 'USD';
    expiresAt: string;
    cardholderName: string;
    billingAddress: RestaurantTenant['address'];
    billingEmail: string;
  }>('/api/subscription/checkout-session', { method: 'POST' }),
  completeSubscriptionPayment: (body: {
    checkoutSessionId: string;
    idempotencyKey: string;
    cardholderName: string;
    termsAccepted: boolean;
    opaqueData: { dataDescriptor: string; dataValue: string };
  }) => request<{ success: true; restaurant: RestaurantTenant }>('/api/subscription/payment', { method: 'POST', body: JSON.stringify(body) }),
  confirmSubscriptionCheckout: (checkoutSessionId: string, transactionId: string) => request<{ success: true; restaurant: RestaurantTenant }>('/api/subscription/checkout-confirmation', { method: 'POST', body: JSON.stringify({ checkoutSessionId, transactionId }) }),
  reconcileSubscription: () => request<{ success: boolean; restaurant: RestaurantTenant }>('/api/subscription/reconcile', { method: 'POST' }),
  session: () => request<{ restaurant: RestaurantTenant; membership: RestaurantMembership }>('/api/session'),
  bootstrap: () => request<BootstrapPayload>('/api/bootstrap', { cache: 'no-store' }),
  checkSupplyIdentity: (body: SupplyIdentityCheckInput) => request<SupplyIdentityCheckResult>('/api/supply-identities/check', { method: 'POST', body: JSON.stringify(body) }),
  mergeSupplies: (body: SupplyMergeInput) => request<SupplyMergeResult>('/api/items/merge', { method: 'POST', body: JSON.stringify(body) }),
  setItemActive: (id: string, active: boolean) => request<SupplyItem>(`/api/items/${encodeURIComponent(id)}/active`, { method: 'PATCH', body: JSON.stringify({ active }) }),
  adjustInventory: (id: string, body: { quantity: number; expectedQuantity: number; reason: string; idempotencyKey: string; initialBaseUnitCost?: number }) => request<InventoryAdjustment>(`/api/items/${encodeURIComponent(id)}/stock-adjustments`, { method: 'POST', body: JSON.stringify(body) }),
  createSupplyRequest: (body: { description: string; categoryId?: string; requestedQuantity: number; inventoryUnit: InventoryUnit; urgency: 'NORMAL' | 'URGENT'; notes?: string; image?: SupplyImageAsset }) => request<SupplyRequest>('/api/supply-requests', { method: 'POST', body: JSON.stringify(body) }),
  reviewSupplyRequest: (id: string, body: { status: 'APPROVED' | 'REJECTED'; existingItemId?: string; departmentIds?: string[]; name?: { en: string; es: string }; categoryId?: string; purchaseUnit?: PackageUnit; inventoryUnit?: InventoryUnit; quantityPerPackage?: number; packageCost?: number; parLevel?: number; rejectionReason?: string }) => request<SupplyRequest>(`/api/supply-requests/${encodeURIComponent(id)}/review`, { method: 'PATCH', body: JSON.stringify(body) }),
  setRecipeActive: (id: string, active: boolean) => request<RestaurantRecipe>(`/api/recipes/${encodeURIComponent(id)}/active`, { method: 'PATCH', body: JSON.stringify({ active }) }),
  deleteRecipe: (id: string) => request<{ success: true }>(`/api/recipes/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  users: () => request<RestaurantUserDirectory>('/api/users'),
  inviteUser: (body: DepartmentAccessInput & { displayName: string; email: string; role: Exclude<RestaurantRole, 'OWNER'>; language: 'en' | 'es' }) => request('/api/users/invitations', { method: 'POST', body: JSON.stringify(body) }),
  updateUserInvitation: (id: string, body: UpdateUserInvitationInput) => request<{ invitation: RestaurantUserInvitation }>(`/api/users/invitations/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resendUserInvitation: (invitationId: string, language: 'en' | 'es') => request(`/api/users/invitations/${encodeURIComponent(invitationId)}/resend`, { method: 'POST', body: JSON.stringify({ language }) }),
  updateUser: (membershipId: string, body: DepartmentAccessInput & { displayName?: string; role?: Exclude<RestaurantRole, 'OWNER'>; active?: boolean }) => request<RestaurantMembership>(`/api/users/${encodeURIComponent(membershipId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  cancelUserInvitation: (invitationId: string) => request(`/api/users/invitations/${encodeURIComponent(invitationId)}/cancel`, { method: 'POST' }),
  uploadRestaurantLogo: (body: { data: string; contentType: 'image/png' }) => request<RestaurantTenant>('/api/restaurant/branding/logo', { method: 'PUT', body: JSON.stringify(body) }),
  removeRestaurantLogo: () => request<RestaurantTenant>('/api/restaurant/branding/logo', { method: 'DELETE' }),
  createCategory: (body: { name: { en: string; es: string }; parentId?: string | null }) => request('/api/categories', { method: 'POST', body: JSON.stringify(body) }),
  createItem: (body: SupplyItemInput) => request<SupplyItem>('/api/items', { method: 'POST', body: JSON.stringify(body) }),
  updateItem: (id: string, body: SupplyItemInput) => request<SupplyItem>(`/api/items/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  suggestSupplyFoodLabels: (body: { name: { en: string; es: string }; categoryId: string; labelText?: string; language?: 'en' | 'es' }) => request<{ tags: FoodTagId[]; source: 'AI' | 'DEMO'; reviewed: false }>('/api/supply-food-labels/suggest', { method: 'POST', body: JSON.stringify(body) }),
  uploadSupplyImage: (body: { data: string; contentType: string; source: SupplyImageSource }) => request<SupplyImageAsset>('/api/supply-images/upload', { method: 'POST', body: JSON.stringify(body) }),
  generateSupplyImage: (body: { name: { en: string; es: string }; categoryId: string }) => request<SupplyImageAsset>('/api/supply-images/generate', { method: 'POST', body: JSON.stringify(body) }),
  reportShortage: (body: { itemId: string; inventoryStatus: Exclude<InventoryStatus, 'IN_STOCK'>; urgency: 'NORMAL' | 'URGENT'; notes?: string }) => request('/api/shortages', { method: 'POST', body: JSON.stringify(body) }),
  updateShortage: (id: string, status: ShortageStatus) => request(`/api/shortages/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  createVendor: (body: Omit<RestaurantVendor, 'id' | 'restaurantId' | 'createdAt' | 'updatedAt'>) => request<RestaurantVendor>('/api/vendors', { method: 'POST', body: JSON.stringify(body) }),
  updateVendor: (id: string, body: Omit<RestaurantVendor, 'id' | 'restaurantId' | 'createdAt' | 'updatedAt'>) => request<RestaurantVendor>(`/api/vendors/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createPurchaseOrder: (body: { vendorId: string; shortageIds: string[]; language: 'en' | 'es'; notes?: string; lines: Array<{ itemId: string; requestedPackages: number; requestedBaseQuantity: number; estimatedPackageCost: number }> }) => request('/api/purchase-orders', { method: 'POST', body: JSON.stringify(body) }),
  updatePurchaseOrder: (id: string, status: PurchaseOrderStatus, receivedLines?: Array<{ itemId: string; receivedPackages: number; receivedBaseQuantity: number; totalCost: number }>) => request(`/api/purchase-orders/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status, receivedLines }) }),
  sharePurchaseOrder: (id: string, body: { channel: 'EMAIL' | 'SMS' | 'WHATSAPP' | 'LINK'; recipient?: string }) => request<{ success: true; shareUrl: string; expiresAt: string }>(`/api/purchase-orders/${encodeURIComponent(id)}/share`, { method: 'POST', body: JSON.stringify(body) }),
  submitInventoryCount: (body: { notes?: string; lines: Array<{ itemId: string; countedQuantity: number }> }) => request('/api/inventory-counts', { method: 'POST', body: JSON.stringify(body) }),
  reviewInventoryCount: (id: string, status: Extract<InventoryCountStatus, 'APPROVED' | 'REJECTED'>, reviewNotes?: string) => request(`/api/inventory-counts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status, reviewNotes }) }),
  createQuickInventoryEntry: (body: { itemId?: string; description: string; categoryId: string; purchaseUnit: PackageUnit; inventoryUnit: InventoryUnit; quantityPerPackage: number; receivedPackages: number; receivedBaseQuantity: number; image?: SupplyImageAsset; notes?: string }) => request('/api/quick-inventory-entries', { method: 'POST', body: JSON.stringify(body) }),
  reviewQuickInventoryEntry: (id: string, body: { status: Extract<QuickInventoryEntryStatus, 'APPROVED' | 'REJECTED'>; departmentIds?: string[]; vendorName?: string; subtotal?: number; tax?: number; rejectionReason?: string }) => request(`/api/quick-inventory-entries/${encodeURIComponent(id)}/review`, { method: 'PATCH', body: JSON.stringify(body) }),
  createDepartment: (body: { name: { en: string; es: string } }) => request('/api/departments', { method: 'POST', body: JSON.stringify(body) }),
  updateDepartment: (id: string, body: { name: { en: string; es: string }; active?: boolean }) => request(`/api/departments/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createRecipe: (body: RecipeInput) => request<RestaurantRecipe>('/api/recipes', { method: 'POST', body: JSON.stringify(body) }),
  updateRecipe: (id: string, body: RecipeInput) => request<RestaurantRecipe>(`/api/recipes/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  uploadRecipeImage: (body: { data: string; contentType: string; source: SupplyImageSource }) => request<SupplyImageAsset>('/api/recipe-images/upload', { method: 'POST', body: JSON.stringify(body) }),
  generateRecipeImage: (body: { name: { en: string; es: string }; itemIds: string[] }) => request<SupplyImageAsset>('/api/recipe-images/generate', { method: 'POST', body: JSON.stringify(body) }),
  createProductionPlan: (body: { departmentId: string; name: { en: string; es: string }; dayOfWeek: number; targetQuantity: number; unit: string; instructions?: string; recipeId?: string; scheduleDate: string; recurrence: ProductionRecurrence; laborAssignments: ProductionLaborAssignment[] }) => request('/api/production-plans', { method: 'POST', body: JSON.stringify(body) }),
  updateProductionPlan: (id: string, body: { departmentId: string; name: { en: string; es: string }; dayOfWeek: number; targetQuantity: number; unit: string; instructions?: string; recipeId?: string; scheduleDate: string; recurrence: ProductionRecurrence; laborAssignments: ProductionLaborAssignment[]; active?: boolean }) => request(`/api/production-plans/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  completeProduction: (body: { planItemId: string; quantityProduced: number; quantityProducedExact?: string; wasteQuantity?: number; wasteQuantityExact?: string; idempotencyKey?: string; notes?: string; laborAssignments?: Array<{ laborProfileId: string; actualHours: number }> }) => request('/api/production-records', { method: 'POST', body: JSON.stringify(body) }),
  createLaborProfile: (body: Omit<LaborProfile, 'id' | 'restaurantId' | 'createdAt' | 'updatedAt'>) => request('/api/labor-profiles', { method: 'POST', body: JSON.stringify(body) }),
  updateLaborProfile: (id: string, body: Omit<LaborProfile, 'id' | 'restaurantId' | 'createdAt' | 'updatedAt'>) => request(`/api/labor-profiles/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createTemperatureUnit: (body: TemperatureUnitInput) => request<TemperatureUnit>('/api/temperature-units', { method: 'POST', body: JSON.stringify(body) }),
  updateTemperatureUnit: (id: string, body: Partial<TemperatureUnitInput>) => request<TemperatureUnit>(`/api/temperature-units/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  recordTemperature: (body: { temperatureUnitId: string; temperature: number; notes?: string }) => request('/api/temperature-logs', { method: 'POST', body: JSON.stringify(body) }),
  createEquipment: (body: EquipmentInput) => request<RestaurantEquipment>('/api/equipment', { method: 'POST', body: JSON.stringify(body) }),
  updateEquipment: (id: string, body: Partial<EquipmentInput>) => request<RestaurantEquipment>(`/api/equipment/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  uploadEquipmentImage: (body: { data: string; contentType: string; source: SupplyImageSource }) => request<SupplyImageAsset>('/api/equipment-images/upload', { method: 'POST', body: JSON.stringify(body) }),
  downloadEquipmentLabels,
  reportEquipmentIssue: (body: { equipmentId: string; severity: 'LOW' | 'MEDIUM' | 'HIGH'; description: string }) => request('/api/equipment-issues', { method: 'POST', body: JSON.stringify(body) }),
  updateEquipmentIssue: (id: string, status: EquipmentIssueStatus) => request(`/api/equipment-issues/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};
